import axios from "axios";
import Cookies from "js-cookie";

/* ----------------------- Axios 設定 ----------------------- */
// 未設定時は同一オリジンで相対パス /api を使用（Cookie 送信のため同一オリジン推奨）
const apiBaseURL = process.env.REACT_APP_API_URL || '/api';
const CSRF_COOKIE_NAME = "csrftoken_v2";
export const api = axios.create({
    baseURL: apiBaseURL,
    withCredentials: true,
});

// 公開用API専用のaxiosインスタンス（認証情報を送信しない）
const publicApi = axios.create({
    baseURL: apiBaseURL,
    withCredentials: false,
});

/** クッキーに CSRF をセットするときのオプション（login / refresh で共通） */
const csrfCookieOptions = () => ({
    path: "/",
    sameSite: "lax",
    secure: window.location.protocol === "https:",
});

/**
 * 同名 cookie が複数あるとき、document.cookie には複数並ぶことがある。
 * Django 側がどちらを採用するか（多くは後勝ち）とズレると CSRF Failed になるため、
 * 最後に出現した値を採用する。
 */
function getCookieLast(name) {
    if (typeof document === "undefined") return undefined;
    const prefix = `${name}=`;
    const raw = document.cookie || "";
    if (!raw) return undefined;
    const parts = raw.split("; ");
    let value;
    for (const part of parts) {
        if (part.startsWith(prefix)) {
            value = part.slice(prefix.length);
        }
    }
    return value ? decodeURIComponent(value) : undefined;
}

/** ブラウザ側で CSRF クッキーのバリアントを掃除する（サーバー側の _clear_csrf_cookie_variants 相当） */
function clearCsrfCookiesInBrowser(name) {
    if (typeof document === "undefined" || typeof window === "undefined") return;
    const host = window.location.hostname || "";
    const parts = host.split(".").filter(Boolean);
    const root =
        parts.length >= 2 ? `${parts[parts.length - 2]}.${parts[parts.length - 1]}` : host;

    // host-only / Domain=host / Domain=.host / 親ドメイン / Domain=.親ドメイン までカバー
    const domains = new Set([undefined, host, `.${host}`]);
    if (root && root !== host) {
        domains.add(root);
        domains.add(`.${root}`);
    }

    const paths = ["/", "/api", "/api/"];
    const expires = "Thu, 01 Jan 1970 00:00:00 GMT";

    domains.forEach((domain) => {
        paths.forEach((path) => {
            let cookie = `${name}=; expires=${expires}; path=${path};`;
            if (domain) cookie += ` domain=${domain};`;
            document.cookie = cookie;
        });
    });
}

/** サーバーから直近で受け取った CSRF トークン。複数 cookie でずれないようリクエスト時はこれを優先する */
let lastCsrfTokenFromServer = null;

api.interceptors.request.use((config) => {
    const token = lastCsrfTokenFromServer ?? getCookieLast(CSRF_COOKIE_NAME);
    if (token) config.headers["X-CSRFToken"] = token;
    return config;
});

/** レスポンスの detail が Django の CSRF 失敗メッセージか */
function isCsrfFailure(error) {
    const detail = error?.response?.data?.detail;
    return typeof detail === "string" && detail.includes("CSRF Failed");
}

/** 同時に複数 403 CSRF が起きても GET login は1回だけにするためのロック */
let csrfRefreshPromise = null;

/**
 * GET /api/accounts/login/ で csrftoken を取得し、Cookie とメモリを更新する。
 * リトライ時に確実に最新トークンで送るため、レスポンス本文の csrfToken も Cookie に反映する。
 */
async function refreshCsrfCookie() {
    try {
        const res = await api.get("accounts/login/", { __isCsrfRefresh: true });
        if (res?.data?.csrfToken) {
            lastCsrfTokenFromServer = res.data.csrfToken;
            Cookies.set(CSRF_COOKIE_NAME, res.data.csrfToken, csrfCookieOptions());
        }
        return res;
    } catch {
        return undefined;
    }
}

/**
 * 403 のうち「CSRF 失敗」ならトークン更新→1回だけリトライ。
 * それ以外の 403 やリトライ失敗は認証エラーとして扱う。
 */
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const is403 = error?.response?.status === 403;
        const isCsrf = is403 && isCsrfFailure(error);
        const canRetry = error.config && !error.config.__isCsrfRefresh && !error.config.__csrfRetried;

        if (isCsrf && canRetry) {
            try {
                // 並行で複数 403 CSRF が出ても GET login は1回にまとめる
                csrfRefreshPromise = csrfRefreshPromise ?? refreshCsrfCookie();
                const refreshed = await csrfRefreshPromise.finally(() => { csrfRefreshPromise = null; });
                if (refreshed?.data?.csrfToken) {
                    const retryConfig = { ...error.config, __csrfRetried: true };
                    // 複数 csrftoken クッキーがあると getCookieLast が別の値を返しうるため、リトライ時はレスポンスで受け取ったトークンを直接ヘッダに載せる
                    retryConfig.headers = { ...retryConfig.headers, "X-CSRFToken": refreshed.data.csrfToken };
                    return api.request(retryConfig);
                }
            } catch (_) {
                // リトライせず下の 403 共通処理へ
            }
        }

        // CSRF 403 が継続する場合はブラウザ側でも古い csrftoken を掃除して自己回復を促す
        if (isCsrf && !canRetry) {
            clearCsrfCookiesInBrowser(CSRF_COOKIE_NAME);
            clearCsrfCookiesInBrowser("csrftoken");
        }

        if (is403) {
            sessionStorage.removeItem("cachedUser");
            if (window.location.pathname === "/login") {
                return Promise.reject(error);
            }
            console.warn("認証エラー (403): セッションが無効です。ログインページに移動してください。");
        }
        return Promise.reject(error);
    }
);

/* ----------------------- 認証 ----------------------- */
/**
 * POST 直前に GET login で最新トークンを取得してから送信する。
 * 複数 csrftoken_v2 が残っている状態でも、GET のレスポンスで受け取ったトークンを
 * lastCsrfTokenFromServer にセットしてから POST するため、サーバーと一致する。
 */
export const login = async (credentials) => {
    const getRes = await api.get("accounts/login/").catch(() => null);
    if (getRes?.data?.csrfToken) {
        lastCsrfTokenFromServer = getRes.data.csrfToken;
        Cookies.set(CSRF_COOKIE_NAME, getRes.data.csrfToken, csrfCookieOptions());
    }
    const res = await api.post("accounts/login/", credentials);
    const data = res.data;
    if (data?.csrfToken) {
        lastCsrfTokenFromServer = data.csrfToken;
        Cookies.set(CSRF_COOKIE_NAME, data.csrfToken, csrfCookieOptions());
    }
    return data;
};

export const logout = () =>
    api.post("accounts/logout/").then((res) => res.data).catch((err) => {
        // 403（CSRF 含む）の場合はサーバ上は未ログイン扱いになり得るため、クライアントはログアウト扱いにする
        if (err?.response?.status === 403) {
            sessionStorage.removeItem("cachedUser");
            return {};
        }
        throw err;
    });

/* ----------------------- ユーザー ----------------------- */
export const fetchCurrentUser = () =>
    api.get("accounts/me/").then((res) => res.data);

/* ----------------------- プロジェクト ----------------------- */
/** 一覧（admin 専用） GET /organizations/projects-list/ */
export const fetchProjects = async (params = {}) => {
    // ページネーション対応: 全ページを自動的に取得
    const allProjects = [];
    let nextUrl = "organizations/projects-list/";
    let pageParams = { ...params };

    while (nextUrl) {
        const response = await api.get(nextUrl, { params: pageParams });
        const data = response.data;

        // ページネーション形式: { count, next, previous, results }
        allProjects.push(...data.results);

        // 次のURLを取得（絶対URLまたは相対パス）
        if (data.next) {
            // 絶対URLの場合は相対パスに変換
            const baseURL = api.defaults.baseURL || '';
            if (data.next.startsWith(baseURL)) {
                nextUrl = data.next.replace(baseURL, '').replace(/^\//, '');
            } else if (data.next.startsWith('http://') || data.next.startsWith('https://')) {
                // 完全なURLの場合は、baseURLを除いた部分を取得
                try {
                    const url = new URL(data.next);
                    nextUrl = url.pathname + url.search;
                    // 先頭のスラッシュを削除
                    if (nextUrl.startsWith('/')) {
                        nextUrl = nextUrl.substring(1);
                    }
                } catch (e) {
                    // URL解析に失敗した場合は、そのまま使用
                    nextUrl = data.next;
                }
            } else {
                // 相対パスの場合
                nextUrl = data.next;
            }
        } else {
            nextUrl = null;
        }

        // 次のリクエストではパラメータをクリア（URLに含まれているため）
        pageParams = {};
    }

    return allProjects;
};

/** 1 件取得 GET /organizations/projects/<id>/ */
export const fetchProject = (projectId) =>
    api.get(`organizations/projects/${projectId}/`).then((res) => res.data);

/** 緯度・経度更新 PATCH /projects/<id>/ */
export const updateProject = (projectId, payload) =>
    api.patch(`organizations/projects/${projectId}/`, payload).then((res) => res.data);

/** プロジェクト詳細更新 PATCH /organizations/projects/<id>/update/ */
export const updateProjectDetails = (projectId, payload) => {
    const formData = new FormData();

    for (const [key, value] of Object.entries(payload)) {
        if (value === null) {
            formData.append(key, '');
        } else if (key === 'thumbnail_image' && value instanceof File) {
            formData.append('thumbnail_image', value);
        } else {
            formData.append(key, value);
        }
    }

    return api.patch(`organizations/projects/${projectId}/update/`, formData).then((res) => res.data);
};

/** プロジェクトのKMLデータ取得 GET /organizations/projects/<id>/kml/ */
export const fetchProjectKmlData = (projectId) =>
    api.get(`organizations/projects/${projectId}/kml/`).then((res) => res.data);

/* ----------------------- 公開用API ----------------------- */
/** 公開プロジェクト取得 GET /organizations/public/<public_url>/ */
export const fetchPublicProject = (publicUrl) =>
    publicApi.get(`organizations/public/${publicUrl}/`).then((res) => res.data);

/** 公開アーカイブロケーション取得 GET /archive-locations/public/<public_url>/archive-locations/ */
export const fetchPublicArchiveLocations = (publicUrl) =>
    publicApi.get(`archive-locations/public/${publicUrl}/archive-locations/`).then((res) => res.data);

/** 公開ラベル取得 GET /archive-locations/public/<public_url>/labels/ */
export const fetchPublicLabels = (publicUrl) =>
    publicApi.get(`archive-locations/public/${publicUrl}/labels/`).then((res) => res.data);

/** 公開プロジェクトのKMLデータ取得 GET /organizations/public/<public_url>/kml/ */
export const fetchPublicProjectKmlData = (publicUrl) =>
    publicApi.get(`organizations/public/${publicUrl}/kml/`).then((res) => res.data);

/** 公開ロケーションのメディア一覧 GET /archive-locations/public/<public_url>/archive-locations/<location_id>/media/ */
export const fetchPublicMediaList = (publicUrl, locationId) =>
    publicApi
        .get(`archive-locations/public/${publicUrl}/archive-locations/${locationId}/media/`)
        .then((res) => res.data);

/* ----------------------- 組織 ----------------------- */
/** 自分が所属する組織一覧 GET /my-organizations/ */
export const fetchMyOrganizations = () =>
    api.get("organizations/my-organizations/").then((res) => res.data);

/* ----------------------- ラベル ----------------------- */
/** 一覧取得 GET /projects/<project_pk>/labels/ */
export const fetchLabels = (projectId, params = {}) =>
    api.get(`/archive-locations/${projectId}/labels/`, { params }).then((res) => res.data);

/** 新規作成 POST */
export const createLabel = (projectId, payload) =>
    api.post(`/archive-locations/${projectId}/labels/`, payload);

/** 更新 PATCH */
export const updateLabel = (projectId, id, payload) =>
    api.patch(`/archive-locations/${projectId}/labels/${id}/`, payload);

/** 削除 DELETE */
export const deleteLabel = (projectId, labelId) =>
    api.delete(`/archive-locations/${projectId}/labels/${labelId}/`).then((res) => res.data);

/* ----------------------- アーカイブロケーション ----------------------- */
/** 一覧取得 GET /projects/<project_pk>/archive-locations/ */
export const fetchArchiveLocations = (projectId, params = {}) =>
    api.get(`/archive-locations/${projectId}/archive-locations/`, { params }).then((res) => res.data);

/** 自分のロケーション一覧 GET /projects/<project_pk>/archive-locations/my/ */
export const fetchMyArchiveLocations = (projectId, params = {}) =>
    api.get(`/archive-locations/${projectId}/archive-locations/my/`, { params }).then((res) => res.data);

/** 1 件取得 GET /archive-locations/<project_pk>/archive-locations/<id>/ */
export const fetchArchiveLocation = (projectId, id) =>
    api.get(`/archive-locations/${projectId}/archive-locations/${id}/`).then((res) => res.data);

/** 新規作成 POST */
export const createArchiveLocation = (projectId, payload) => {
    const formData = buildFormData(payload);
    return api.post(`/archive-locations/${projectId}/archive-locations/`, formData);
};
/** 更新 PATCH */
export const updateArchiveLocation = (projectId, id, payload) => {
    const formData = buildFormData(payload);
    return api.patch(`/archive-locations/${projectId}/archive-locations/${id}/`, formData);
};

/** 削除 DELETE */
export const deleteArchiveLocation = (projectId, id) =>
    api.delete(`/archive-locations/${projectId}/archive-locations/${id}/`).then((res) => res.data);

/* ----------------------- メディア ----------------------- */
/** 一覧取得 GET /media/ (location指定可) */
export const fetchMediaList = (locationId, params = {}) => {
    const query = { ...params };
    if (locationId) query.location = locationId;
    return api.get("/archive-locations/media/", { params: query }).then((res) => res.data);
};

/** 詳細取得 GET /media/<id>/ */
export const fetchMediaDetail = (id) =>
    api.get(`/archive-locations/media/${id}/`).then((res) => res.data);

/** アップロード POST /media/ */
export const uploadMedia = (file, locationId, name, description, mediaType) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("location", locationId);
    formData.append("name", name);
    formData.append("description", description);
    formData.append("media_type", mediaType);

    return api.post("archive-locations/media/", formData).then((res) => res.data);
};

/** メタ情報更新 PATCH /media/<id>/ */
export const updateMediaMeta = (mediaId, { name, description }) =>
    api.patch(`archive-locations/media/${mediaId}/`, { name, description }).then((res) => res.data);

/** 削除 DELETE /media/<id>/ */
export const deleteMedia = (id) =>
    api.delete(`/archive-locations/media/${id}/`).then((res) => res.data);

/** ダイレクトアップロード用の署名付きURL取得 POST /media/direct-upload/ */
export const getDirectUploadUrl = (locationId, filename, contentType, fileSize) => {
    return api.post('/archive-locations/media/direct-upload/', {
        location_id: locationId,
        filename: filename,
        content_type: contentType,
        file_size: fileSize
    }).then((res) => res.data);
};

/** ダイレクトアップロード完了通知 POST /media/direct-upload/complete/ */
export const completeDirectUpload = (locationId, uploadPath, name, description, mediaType, sessionId = null) => {
    const payload = {
        location_id: locationId,
        upload_path: uploadPath,
        name: name,
        description: description,
        media_type: mediaType
    };

    // session_idが提供されている場合は追加
    if (sessionId) {
        payload.session_id = sessionId;
    }

    return api.post('/archive-locations/media/direct-upload/complete/', payload).then((res) => res.data);
};

/* ----------------------- AI Chat ----------------------- */
/** プロジェクトAI設定取得 GET /api/ai-chat/projects/<project_id>/config/ */
export const fetchProjectAIConfig = (projectId) =>
    api.get(`ai-chat/projects/${projectId}/config/`).then((res) => res.data);

/** プロジェクトAI設定更新 PUT/PATCH /api/ai-chat/projects/<project_id>/config/ */
export const updateProjectAIConfig = (projectId, payload) =>
    api.patch(`ai-chat/projects/${projectId}/config/`, payload).then((res) => res.data);

/**
 * プロジェクトに関連する会話履歴をlocalStorageからクリア
 * @param {string|number} projectId - プロジェクトID
 * @param {string} userLoginId - ユーザーのログインID
 */
export const clearProjectConversationHistory = (projectId, userLoginId) => {
    if (!userLoginId) return;

    try {
        // 会話IDとメッセージをクリア
        const convKey = `ai:conv:${userLoginId}`;
        const msgsKey = `ai:msgs:${userLoginId}`;

        localStorage.removeItem(convKey);
        localStorage.removeItem(msgsKey);

        // 削除されたメッセージIDの記録もクリア
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith(`ai:deleted:${userLoginId}:`)) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.error('会話履歴のクリアに失敗しました:', error);
    }
};

/** 会話一覧取得 GET /api/ai-chat/projects/<project_id>/conversations/ */
export const fetchAIConversations = (projectId) =>
    api.get(`ai-chat/projects/${projectId}/conversations/`).then((res) => res.data);

/** 新規会話作成 POST /api/ai-chat/projects/<project_id>/conversations/ */
export const createAIConversation = (projectId, payload) =>
    api.post(`ai-chat/projects/${projectId}/conversations/`, payload).then((res) => res.data);

/** 会話詳細取得 GET /api/ai-chat/conversations/<pk>/ */
export const fetchAIConversation = (conversationId) =>
    api.get(`ai-chat/conversations/${conversationId}/`).then((res) => res.data);

/** 会話削除 DELETE /api/ai-chat/conversations/<pk>/ */
export const deleteAIConversation = (conversationId) =>
    api.delete(`ai-chat/conversations/${conversationId}/`).then((res) => res.data);

/** メッセージ送信受付（202） POST /api/ai-chat/projects/<project_id>/messages/ */
export const aiChatMessageRequestCreate = (projectId, payload, signal = null) => {
    const requestOptions = signal ? { signal } : {};
    return api.post(`ai-chat/projects/${projectId}/messages/`, payload, requestOptions).then((res) => res.data);
};

/** メッセージリクエスト状態取得（ポーリング） GET /api/ai-chat/projects/<project_id>/messages/<message_id>/ */
export const aiChatMessageRequestPoll = (projectId, messageId, signal = null) => {
    const requestOptions = signal ? { signal } : {};
    return api.get(`ai-chat/projects/${projectId}/messages/${messageId}/`, requestOptions).then((res) => res.data);
};

/** メッセージ一覧取得 GET /api/ai-chat/projects/<project_id>/messages/（旧: conversation_id クエリ） */
export const fetchAIMessages = (projectId, conversation_id, limit = 20) =>
    api.get(`ai-chat/projects/${projectId}/messages/`, {
        params: { conversation_id, limit }
    }).then((res) => res.data);

/** Opening 取得 GET /api/ai-chat/projects/<project_id>/models/<model_id>/opening/ */
export const fetchAIOpening = (projectId, modelId, params = {}, signal = null) => {
    const requestOptions = signal ? { signal } : {};
    const query = { age: params.age };
    if (params.disaster_type != null && params.disaster_type !== '') query.disaster_type = params.disaster_type;
    if (params.tsunami != null && params.tsunami !== '') query.tsunami = params.tsunami;
    return api.get(`ai-chat/projects/${projectId}/models/${modelId}/opening/`, {
        params: query,
        ...requestOptions
    }).then((res) => res.data);
};

/** 会話メッセージ一覧取得 GET .../conversations/<id>/messages/（新仕様） */
export const fetchAIConversationMessages = (projectId, conversationId, limit = 20, signal = null) => {
    const requestOptions = signal ? { signal } : {};
    return api.get(`ai-chat/projects/${projectId}/conversations/${conversationId}/messages/`, {
        params: { limit },
        ...requestOptions
    }).then((res) => res.data);
};

/** 同期メッセージ送信 POST .../conversations/<id>/messages/（201 で output を返す） */
export const sendAIChatMessage = (projectId, conversationId, payload, signal = null) => {
    const requestOptions = signal ? { signal } : {};
    return api.post(`ai-chat/projects/${projectId}/conversations/${conversationId}/messages/`, payload, requestOptions).then((res) => res.data);
};

// パラメータ取得のキャッシュ（project_idごと）
const parametersCache = new Map();
const PARAMETERS_CACHE_DURATION = 5 * 60 * 1000; // 5分

/** パラメータ取得 GET /api/ai-chat/projects/<project_id>/parameters/ */
export const fetchAIParameters = async (projectId) => {
    const now = Date.now();
    const cacheKey = `params_${projectId}`;
    const cached = parametersCache.get(cacheKey);

    // キャッシュが有効な場合はキャッシュを返す
    if (cached && cached.time && (now - cached.time) < PARAMETERS_CACHE_DURATION) {
        return cached.data;
    }

    // キャッシュが無効または存在しない場合はAPIを呼び出し
    const data = await api.get(`ai-chat/projects/${projectId}/parameters/`).then((res) => res.data);
    parametersCache.set(cacheKey, { data, time: now });
    return data;
};

/** パラメータキャッシュをクリア */
export const clearAIParametersCache = (projectId = null) => {
    if (projectId) {
        const cacheKey = `params_${projectId}`;
        parametersCache.delete(cacheKey);
    } else {
        parametersCache.clear();
    }
};

/** デバッグログをバックエンドに送信 */

function buildFormData(payload) {
    const formData = new FormData();

    for (const [key, value] of Object.entries(payload)) {
        if (value === null) {
            formData.append(key, '');
        } else if (key === 'thumbnail' && value instanceof File) {
            formData.append('thumbnail', value);
        } else {
            formData.append(key, value);
        }
    }

    return formData;
}

export default api;
